var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { enumerable } from '../meta';
import { nodeToString } from '../tree';
import { SelectionGedcom } from './internal';
/**
 * A selection of Gedcom nodes, represented in an array-like datastructure.
 */
var SelectionAny = /** @class */ (function () {
    /**
     * @param rootNode
     * @param nodes
     * @category Base
     */
    function SelectionAny(rootNode, nodes) {
        this.rootNode = rootNode;
        this.length = nodes.length;
        for (var i = 0; i < nodes.length; i++) {
            this[i] = nodes[i];
        }
    }
    /**
     * Returns a constructor for this selection.
     * @private
     * @category Base
     */
    SelectionAny.prototype.selfConstructor = function () {
        if (Object.getPrototypeOf !== undefined) {
            return Object.getPrototypeOf(this).constructor;
        }
        else {
            return this.__proto__.constructor; // eslint-disable-line no-proto
        }
    };
    /**
     * Returns an array of {@link TreeNode.tag}.
     * @category Base
     */
    SelectionAny.prototype.tag = function () {
        var array = [];
        for (var i = 0; i < this.length; i++) {
            array.push(this[i].tag);
        }
        return array;
    };
    /**
     * Returns an array of {@link TreeNode.pointer}.
     * @category Base
     */
    SelectionAny.prototype.pointer = function () {
        var array = [];
        for (var i = 0; i < this.length; i++) {
            array.push(this[i].pointer);
        }
        return array;
    };
    /**
     * Returns an array of {@link TreeNode.value}.
     * @category Base
     */
    SelectionAny.prototype.value = function () {
        var array = [];
        for (var i = 0; i < this.length; i++) {
            array.push(this[i].value);
        }
        return array;
    };
    /**
     * Calls {@link value} and filters out <code>null</code> values.
     * @category Base
     */
    SelectionAny.prototype.valueNonNull = function () {
        return this.value().filter(function (v) { return v !== null; });
    };
    /**
     * Wraps the value of {@link rootNode} in {@link SelectionGedcom}.
     * The selection will contain exactly one node.
     * @category Base
     */
    SelectionAny.prototype.root = function () {
        return new SelectionGedcom(this.rootNode, [this.rootNode]);
    };
    // Implementation
    SelectionAny.prototype.get = function (tag, pointer, adapter) {
        var Adapter = adapter != null ? adapter : SelectionAny; // Type safety of this cast is enforced by the signature of the visible methods
        var tags = tag != null ? (Array.isArray(tag) ? tag : [tag]) : null;
        var pointers = pointer != null ? (Array.isArray(pointer) ? pointer : [pointer]) : null;
        var selection = [];
        var _loop_1 = function (i) {
            var node = this_1[i];
            if (tags === null && pointers === null) { // No need of index
                node.children.forEach(function (child) { return selection.push(child); });
            }
            else if (node._index !== undefined) { // Use index
                var index_1 = node._index;
                var intermediary_1 = [];
                var requiresSorting = (tags !== null && tags.length > 0) || (pointers !== null && pointers.length > 0);
                if (pointers !== null) {
                    var rootIndex_1 = index_1;
                    if (rootIndex_1.byTagPointer !== undefined) { // If the cast is unsafe then the selection should be empty
                        if (tags !== null) {
                            tags.forEach(function (t) { return pointers.forEach(function (p) {
                                var childIdx = rootIndex_1.byTagPointer[t][p];
                                if (childIdx !== undefined) {
                                    intermediary_1.push(node.children[childIdx]);
                                }
                            }); });
                        }
                        else {
                            Object.values(rootIndex_1.byTagPointer).forEach(function (nodes) { return pointers.forEach(function (p) {
                                var childIdx = nodes[p];
                                if (childIdx !== undefined) {
                                    intermediary_1.push(node.children[childIdx]);
                                }
                            }); });
                        }
                    }
                }
                else {
                    var tagsNonNull = tags; // Safe cast
                    tagsNonNull.forEach(function (t) {
                        var nodesIdx = index_1.byTag[t];
                        if (nodesIdx !== undefined) {
                            nodesIdx.forEach(function (childIdx) { return intermediary_1.push(node.children[childIdx]); });
                        }
                    });
                }
                if (requiresSorting) {
                    intermediary_1.sort(function (a, b) { return a.indexSource - b.indexSource; });
                }
                intermediary_1.forEach(function (child) { return selection.push(child); });
            }
            else { // No index fallback
                node.children.filter(function (child) {
                    return (tags === null || (child.tag !== null && tags.includes(child.tag))) &&
                        (pointers === null || (child.pointer !== null && pointers.includes(child.pointer)));
                }).forEach(function (child) { return selection.push(child); });
            }
        };
        var this_1 = this;
        for (var i = 0; i < this.length; i++) {
            _loop_1(i);
        }
        return new Adapter(this.rootNode, selection);
    };
    /**
     * Filter nodes from the selection based on a predicate.
     * @param f The filtering predicate
     * @category Base
     */
    SelectionAny.prototype.filter = function (f) {
        var nodes = [];
        for (var i = 0; i < this.length; i++) {
            var node = this[i];
            if (f(node)) {
                nodes.push(node);
            }
        }
        var Constructor = this.selfConstructor();
        return new Constructor(this.rootNode, nodes);
    };
    /**
     * Filter lifted nodes from the selection based on a predicate.
     * The argument is a selection of one node.
     * @param f The filtering predicate
     * @category Base
     */
    SelectionAny.prototype.filterSelect = function (f) {
        var Constructor = this.selfConstructor();
        var nodes = [];
        for (var i = 0; i < this.length; i++) {
            var node = this[i];
            if (f(new Constructor(this.rootNode, [node]))) {
                nodes.push(node);
            }
        }
        return new Constructor(this.rootNode, nodes);
    };
    /**
     * View this selection as a different type. This method can be used to extend functionality for non-standard Gedcom files.
     * @param Adapter The class adapter
     * @category Base
     */
    SelectionAny.prototype.as = function (Adapter) {
        return new Adapter(this.rootNode, this);
    };
    /**
     * Export the selection as an array of nodes.
     * The inverse operation is {@link of}.
     * @category Base
     */
    SelectionAny.prototype.array = function () {
        var array = [];
        for (var i = 0; i < this.length; i++) {
            array.push(this[i]);
        }
        return array;
    };
    /**
     * Exports the selection as an array of selections of one element.
     * @category Base
     */
    SelectionAny.prototype.arraySelect = function () {
        var Constructor = this.selfConstructor();
        var array = [];
        for (var i = 0; i < this.length; i++) {
            array.push(new Constructor(this.rootNode, [this[i]]));
        }
        return array;
    };
    /**
     * Returns a concatenation of two selections.
     * The right hand side selection should be a subtype of the left hand side's.
     * The resulting selection will be the same type as the left hand side's.
     * @param other The right hand side selection
     * @category Base
     */
    SelectionAny.prototype.concatenate = function (other) {
        var Constructor = this.selfConstructor();
        var nodes = [];
        for (var i = 0; i < this.length; i++) {
            nodes.push(this[i]);
        }
        for (var i = 0; i < other.length; i++) {
            nodes.push(other[i]);
        }
        return new Constructor(this.rootNode, nodes);
    };
    /**
     * Returns a concatenation of two selections.
     * The right hand side selection should be a subtype of the left hand side's.
     * The resulting selection will be the same type as the left hand side's, with the elements of the right hand side's first.
     * @param other The right hand side selection
     * @category Base
     */
    SelectionAny.prototype.concatenateLeft = function (other) {
        var Constructor = this.selfConstructor();
        var nodes = [];
        for (var i = 0; i < other.length; i++) {
            nodes.push(other[i]);
        }
        for (var i = 0; i < this.length; i++) {
            nodes.push(this[i]);
        }
        return new Constructor(this.rootNode, nodes);
    };
    /**
     * Returns a sorted selection, with respect to the comparator.
     * The default comparator relies on the {@link TreeNode.indexSource} attribute.
     * @param comparator The comparator
     * @category Base
     */
    SelectionAny.prototype.sorted = function (comparator) {
        if (comparator === void 0) { comparator = function (a, b) { return a.indexSource - b.indexSource; }; }
        var Constructor = this.selfConstructor();
        var nodes = [];
        for (var i = 0; i < this.length; i++) {
            nodes.push(this[i]);
        }
        nodes.sort(comparator);
        return new Constructor(this.rootNode, nodes);
    };
    /**
     * Checks whether two selections are equal.
     * Note that the strategy used here is reference equality, hence for this method to return <code>true</code>, the nodes must be the same references (and in the same order).
     * @param other The selection to compare it against
     * @category Base
     */
    SelectionAny.prototype.equals = function (other) {
        if (this.length !== other.length) {
            return false;
        }
        for (var i = 0; i < this.length; i++) {
            if (this[i] !== other[i]) {
                return false;
            }
        }
        return true;
    };
    /**
     * Returns a string representation for this selection.
     * @category Base
     */
    SelectionAny.prototype.toString = function () {
        return this.length > 0 ? this.array().map(nodeToString).join('\n\n') : '(empty selection)';
    };
    // Implementation
    SelectionAny.of = function (previous, nodes, Adapter) {
        var AdapterClass = Adapter != null ? Adapter : SelectionAny;
        var nodesArray = Array.isArray(nodes) ? nodes : [nodes];
        return new AdapterClass(previous.rootNode, nodesArray);
    };
    __decorate([
        enumerable(false),
        __metadata("design:type", Object)
    ], SelectionAny.prototype, "rootNode", void 0);
    return SelectionAny;
}());
export { SelectionAny };
//# sourceMappingURL=SelectionAny.js.map